import { ConsoleLogger } from '@proposify/frontend-observability';

/**
 * Reorders an array of items based on a drag-and-drop operation simulation.
 *
 * @template T The type of items in the array. Must have an `id` property.
 * @param items The original array of items.
 * @param fromIndex Initial integer index of the item being dragged.
 * @param toIndex Target position (a half-integer) between two items (e.g., 2.5 means between indices 2 and 3).
 * @returns A new array containing objects with `id` and `index` properties representing the reordered sequence.
 * @throws {Error} If fromIndex is not an integer or toIndex is not a half-integer.
 * @throws {Error} If fromIndex is out of bounds.
 */
export function reorderItems<T extends { id: string }>(items: T[], fromIndex: number, toIndex: number): { id: string; index: number }[] {
  if (toIndex % 1 === 0) throw new Error('toIndex must be a half-integer (e.g., 0.5, 1.5, 2.5)');
  if (fromIndex % 1 !== 0) throw new Error('fromIndex must be an integer');

  ConsoleLogger.log(`Reordering item from ${fromIndex} to ${toIndex}`);

  const newSequence = items.map(({ id }, index) => ({ id, index }));
  newSequence[fromIndex].index = toIndex;
  newSequence.sort((a, b) => a.index - b.index);

  ConsoleLogger.log(
    `New sequence: ${JSON.stringify(newSequence.map((item) => items.find((i) => i.id === item.id)?.['title'] ?? item.id))}`
  );

  return newSequence;
}

/**
 * Detects what item moved and after which item it was placed when comparing two arrays.
 *
 * @template T Type of array items, must have an id property
 * @param {T[]} A1 Original array of items
 * @param {T[]} A2 Reordered array of items
 * @returns {{ whatMovedId: string; afterWhatId: string | null }} Object containing:
 *   - whatMovedId: ID of the item that was moved
 *   - afterWhatId: ID of the item after which the moved item was placed, or null if moved to start
 * @throws {Error} If arrays have different lengths
 * @throws {Error} If no reordering is detected
 */
export function detectReordering<T extends Partial<T> & { id: string }>(
  A1: T[],
  A2: T[]
): { whatMovedId: string; afterWhatId: string | null } {
  if (A1.length !== A2.length) throw new Error('A1 and A2 must have the same length');

  const findWhatsBefore = (id: string) => {
    const index = A2.findIndex((item) => item.id === id);
    return index === 0 ? null : A2[index - 1]?.id;
  };

  for (let i = 0; i < A1.length - 1; i++) {
    if (A1[i].id === A2[i].id) {
      continue;
    }

    if (A1[i + 1].id === A2[i].id) {
      ConsoleLogger.log(`Moved ${A1[i].id} after ${findWhatsBefore(A1[i].id)}`);
      return { whatMovedId: A1[i].id, afterWhatId: findWhatsBefore(A1[i].id) };
    }

    if (A1[i].id === A2[i + 1].id) {
      ConsoleLogger.log(`Moved ${A2[i].id} after ${i === 0 ? null : A2[i - 1].id}`);
      return { whatMovedId: A2[i].id, afterWhatId: i === 0 ? null : A2[i - 1].id };
    }

    continue;
  }

  throw new Error(
    'No reordering detected' +
      JSON.stringify(A1.map((item) => item['title'] ?? item.id)) +
      JSON.stringify(A2.map((item) => A1.find((i) => i.id === item.id)?.['title'] ?? item.id))
  );
}
